/**
 * The student area's entry: boots the session from the stored refresh token (ADR 0006), loads
 * `GET /config` once for the day goal and the category label (spec 3.3), and then hands over to
 * the feed. Without a session it sends the student to the login screen — the single navigate
 * entry point of ADR 0005.
 */

import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { API_URL, publicJson } from "../shared/api.js";
import type { Config } from "../shared/api-types.js";
import { getSession, refreshSession } from "../shared/auth-store.js";
import { germanMessage } from "../shared/errors.js";
import { FeedScreen } from "./feed/FeedScreen.js";
import { OfflineState, Splash } from "./states.js";

type Boot =
  | { status: "booting" }
  | { status: "anonymous" }
  | { status: "teacher" }
  | { status: "error"; message: string }
  | { status: "ready"; config: Config };

export function StudentHome() {
  const [boot, setBoot] = useState<Boot>({ status: "booting" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = getSession()?.accessToken ?? (await refreshSession(API_URL, "student"));
        if (cancelled) return;
        if (!token) {
          setBoot({ status: "anonymous" });
          return;
        }
        // The mirror of `TeacherLayout`'s student guard (build spec 5). Both areas share one
        // browser and one stored token, so a teacher who opens the feed is ordinary — and
        // without this their teacher token would fetch `/feed`, get 401, and land them on
        // "Keine Verbindung / Du bist nicht mehr angemeldet": both statements untrue, and a
        // retry that can never succeed.
        if (getSession()?.subjectType === "teacher") {
          setBoot({ status: "teacher" });
          return;
        }
        const config = await publicJson<Config>("/config");
        if (cancelled) return;
        setBoot({ status: "ready", config });
      } catch (err) {
        if (!cancelled) setBoot({ status: "error", message: germanMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  if (boot.status === "booting") return <Splash />;
  if (boot.status === "anonymous") return <Navigate to="/login" replace />;
  if (boot.status === "teacher") return <Navigate to="/lehrer" replace />;
  if (boot.status === "error") {
    return (
      <OfflineState
        onRetry={() => {
          setBoot({ status: "booting" });
          setAttempt((value) => value + 1);
        }}
        message={boot.message}
      />
    );
  }

  return (
    <FeedScreen categoryLabel={boot.config.categories.geschichte} dayGoal={boot.config.dayGoal} />
  );
}
